import { createFileRoute } from '@tanstack/react-router';
import { toast } from 'sonner';
import { useState, useEffect, useMemo, useRef } from 'react';
import { ClipboardList, Trophy } from 'lucide-react';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { useAuth } from '../hooks/useAuth';
import { can } from '../lib/roles';
import { DEFAULT_COMPANY_ID } from '../lib/config';
import { listUsers, type UserDto } from '../lib/users';
import {
  startIDPGeneration,
  getIDPReadiness,
  getIDPsForUser,
  listAllIDPs,
  updateIDPStatus,
  updateIDPContent,
  updateMilestoneStatus,
  type IDPPlan,
  type IDPObjective,
  type IDPMilestone,
  type CompetencyGap,
  type IDPReadinessResult,
} from '../lib/idp';
import { processAIJob } from '../lib/aiQueue';
import { printDocument, downloadJSON, buildIDPPrintHTML } from '../lib/exportUtils';

export const Route = createFileRoute('/idp' as any)({
  component: IDPPage,
});

// ─── Export dropdown ─────────────────────────────────────────────────────────

function ExportDropdown({ onPDF, onJSON }: { onPDF: () => void; onJSON: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)}
        className="text-xs px-3 py-1.5 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 flex items-center gap-1">
        Export ▾
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-36 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
          <button type="button" onClick={() => { onPDF(); setOpen(false); }}
            className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50">Export as PDF</button>
          <button type="button" onClick={() => { onJSON(); setOpen(false); }}
            className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50">Export as JSON</button>
        </div>
      )}
    </div>
  );
}

// ─── Severity badge ───────────────────────────────────────────────────────────

function SeverityBadge({ severity }: { severity: CompetencyGap['severity'] }) {
  const styles = {
    high: 'bg-red-50 text-red-700 border-red-200',
    medium: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    low: 'bg-green-50 text-green-700 border-green-200',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${styles[severity]}`}>
      {severity}
    </span>
  );
}

// ─── Level bar ────────────────────────────────────────────────────────────────

function LevelBar({ current, target }: { current: number; target: number }) {
  return (
    <div className="flex items-center gap-2 mt-1">
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((lvl) => (
          <div key={lvl} className={`w-4 h-2 rounded-sm ${
            lvl <= current ? 'bg-blue-500' : lvl <= target ? 'bg-blue-200' : 'bg-gray-100'
          }`} title={lvl <= current ? 'Current' : lvl <= target ? 'Target' : ''} />
        ))}
      </div>
      <span className="text-xs text-gray-500">L{current} → L{target}</span>
    </div>
  );
}

// ─── Milestone status badge (clickable) ──────────────────────────────────────

const MILESTONE_CYCLE: Record<IDPMilestone['status'], IDPMilestone['status']> = {
  'pending': 'in-progress',
  'in-progress': 'completed',
  'completed': 'pending',
};
const MILESTONE_STYLES: Record<IDPMilestone['status'], string> = {
  pending: 'bg-gray-100 text-gray-500',
  'in-progress': 'bg-yellow-50 text-yellow-700',
  completed: 'bg-green-50 text-green-700',
};
const MILESTONE_LABELS: Record<IDPMilestone['status'], string> = {
  pending: 'Pending',
  'in-progress': 'In Progress',
  completed: 'Done',
};

function MilestoneBadge({
  status, onClick, canEdit,
}: {
  status: IDPMilestone['status'];
  onClick?: () => void;
  canEdit?: boolean;
}) {
  const cls = `text-xs px-2 py-0.5 rounded-full font-medium ${MILESTONE_STYLES[status]} ${canEdit ? 'cursor-pointer hover:opacity-70 transition-opacity' : ''}`;
  if (canEdit && onClick) {
    return (
      <button type="button" onClick={onClick} className={cls} title="Click to update status">
        {MILESTONE_LABELS[status]}
      </button>
    );
  }
  return <span className={cls}>{MILESTONE_LABELS[status]}</span>;
}

// ─── IDP Plan viewer ──────────────────────────────────────────────────────────

function IDPView({
  plan,
  canEditMilestones,
  onMilestoneClick,
}: {
  plan: IDPPlan;
  canEditMilestones: boolean;
  onMilestoneClick: (idx: number) => void;
}) {
  return (
    <div className="space-y-6">
      <p className="text-xs text-gray-500">
        Generated {new Date(plan.generatedAt).toLocaleString()} · Status:{' '}
        <span className="font-medium capitalize">{plan.status}</span>
        {plan.lastEditedAt && (
          <span className="ml-2 text-gray-400">· Edited {new Date(plan.lastEditedAt).toLocaleDateString()}</span>
        )}
      </p>

      {/* Competency Gaps / Advancement-ready — read-only, AI-owned */}
      {(() => {
        // Client-side safety filter: strip any gaps where current >= target
        const realGaps = plan.competencyGaps.filter((g) => g.currentLevel < g.targetLevel);
        const isAdvancementReady = plan.readyForAdvancement === true || (plan.competencyGaps.length > 0 && realGaps.length === 0);

        if (isAdvancementReady && realGaps.length === 0) {
          // All current-role gaps met AND no next-role gaps available
          return (
            <div className="rounded-xl border border-green-200 bg-green-50 p-4 flex items-start gap-3">
              <div className="shrink-0 w-9 h-9 rounded-full bg-green-100 flex items-center justify-center">
                <Trophy className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-semibold text-green-800">Ready for Advancement</p>
                <p className="text-xs text-green-700 mt-0.5">
                  All competency requirements are met or exceeded. This employee is a candidate for promotion to the next level.
                  No career path is configured yet — the plan below outlines general stretch goals.
                </p>
              </div>
            </div>
          );
        }

        // Either has current-role gaps, or advancement-ready with next-role gaps shown
        const sectionTitle = isAdvancementReady ? 'Next Role Gap Analysis' : 'Competency Gaps';
        const borderColor = isAdvancementReady ? 'border-green-200' : 'border-gray-200';
        const bgColor = isAdvancementReady ? 'bg-green-50/50' : 'bg-white';

        return (
          <div>
            {isAdvancementReady && (
              <div className="rounded-xl border border-green-200 bg-green-50 p-3 flex items-center gap-3 mb-3">
                <div className="shrink-0 w-7 h-7 rounded-full bg-green-100 flex items-center justify-center">
                  <Trophy className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-green-800">Ready for Advancement — focusing on next role targets</p>
                  <p className="text-xs text-green-700">Current role requirements fully met. Gaps below show what's needed for the next role.</p>
                </div>
              </div>
            )}
            <h3 className="text-sm font-semibold text-gray-800 mb-3">{sectionTitle}</h3>
            <div className="space-y-3">
              {realGaps.map((gap) => (
                <div key={gap.code} className={`border ${borderColor} rounded-lg p-3 ${bgColor}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-800">
                      {gap.name} <span className="text-gray-400 font-mono text-xs">({gap.code})</span>
                    </span>
                    <SeverityBadge severity={gap.severity} />
                  </div>
                  <LevelBar current={gap.currentLevel} target={gap.targetLevel} />
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Development Objectives / Stretch Goals */}
      {(() => {
        const isAdvancementReady = plan.readyForAdvancement === true;
        const objTitle = isAdvancementReady ? 'Stretch Goals for Next Role' : 'Development Objectives';
        const borderColor = isAdvancementReady ? 'border-green-300' : 'border-blue-200';
        return (
          <div>
            <h3 className="text-sm font-semibold text-gray-800 mb-3">{objTitle}</h3>
            {plan.objectives.length === 0 ? (
              <p className="text-sm text-gray-400">No objectives defined.</p>
            ) : (
              <div className="space-y-4">
                {plan.objectives.map((obj, i) => (
                  <div key={i} className={`border-l-2 ${borderColor} pl-4`}>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${isAdvancementReady ? 'bg-green-50 text-green-700' : 'bg-blue-50 text-blue-700'}`}>{obj.competency}</span>
                      <span className="text-xs text-gray-400">{obj.timeline}</span>
                    </div>
                    <p className="text-sm text-gray-700 mb-2">{obj.goal}</p>
                    {Array.isArray(obj.resources) && obj.resources.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {obj.resources.map((r, ri) => (
                          <span key={ri} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{r}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })()}

      {/* Milestones */}
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-3">
          {plan.readyForAdvancement ? 'Advancement Milestones' : '30 / 60 / 90 Day Milestones'}
        </h3>
        {plan.milestones.length === 0 ? (
          <p className="text-sm text-gray-400">No milestones defined.</p>
        ) : (
          <div className="space-y-2">
            {plan.milestones.map((m, i) => (
              <div key={i} className="flex items-start gap-3 border rounded-lg px-3 py-2.5 bg-white">
                <div className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-xs flex items-center justify-center font-bold shrink-0 mt-0.5">
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-700">{m.label}</p>
                  {m.dueDate && (
                    <p className="text-xs text-gray-400 mt-0.5">
                      Due: {new Date(m.dueDate).toLocaleDateString()}
                    </p>
                  )}
                </div>
                <MilestoneBadge
                  status={m.status}
                  canEdit={canEditMilestones}
                  onClick={() => onMilestoneClick(i)}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Manager Notes — visible to all */}
      {plan.notes && plan.notes.trim() && (
        <div className="border-l-4 border-teal-400 bg-teal-50 rounded-r-lg px-4 py-3">
          <p className="text-xs font-semibold text-teal-700 mb-1">Notes from your manager</p>
          <p className="text-sm text-teal-900 whitespace-pre-wrap">{plan.notes}</p>
        </div>
      )}
    </div>
  );
}

// ─── Edit IDP Modal ───────────────────────────────────────────────────────────

function EditIDPModal({
  plan,
  companyId,
  editorUserId,
  onSave,
  onClose,
}: {
  plan: IDPPlan;
  companyId: string;
  editorUserId: string;
  onSave: (updated: IDPPlan) => void;
  onClose: () => void;
}) {
  const [objectives, setObjectives] = useState<IDPObjective[]>(
    plan.objectives.map((o) => ({ ...o, resources: [...o.resources] })),
  );
  const [milestones, setMilestones] = useState<IDPMilestone[]>(
    plan.milestones.map((m) => ({ ...m })),
  );
  const [notes, setNotes] = useState(plan.notes ?? '');
  const [saving, setSaving] = useState(false);
  const [newResource, setNewResource] = useState<Record<number, string>>({});

  async function handleSave() {
    setSaving(true);
    const res = await updateIDPContent(plan.id, companyId, editorUserId, plan.userId, {
      objectives,
      milestones,
      notes: notes.trim() || null,
    });
    setSaving(false);
    if (res.success && res.data?.plan) {
      toast.success('Development plan updated.');
      onSave(res.data.plan);
    } else {
      toast.error(res.error ?? 'Failed to save changes');
    }
  }

  function updateObjective(i: number, field: keyof IDPObjective, value: string | string[]) {
    setObjectives((prev) => prev.map((o, idx) => idx === i ? { ...o, [field]: value } : o));
  }

  function addResource(i: number) {
    const val = (newResource[i] ?? '').trim();
    if (!val) return;
    updateObjective(i, 'resources', [...objectives[i].resources, val]);
    setNewResource((prev) => ({ ...prev, [i]: '' }));
  }

  function removeResource(objIdx: number, resIdx: number) {
    updateObjective(objIdx, 'resources', objectives[objIdx].resources.filter((_, ri) => ri !== resIdx));
  }

  function updateMilestone(i: number, field: keyof IDPMilestone, value: string) {
    setMilestones((prev) => prev.map((m, idx) => idx === i ? { ...m, [field]: value } : m));
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b shrink-0">
          <h2 className="text-sm font-semibold text-gray-900">Edit Development Plan</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none">✕</button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-6">

          {/* Objectives */}
          <div>
            <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">Development Objectives</h3>
            <div className="space-y-5">
              {objectives.map((obj, i) => (
                <div key={i} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{obj.competency}</span>
                    <span className="text-xs text-gray-400">competency (read-only)</span>
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Goal</label>
                    <textarea
                      rows={2}
                      className="w-full border rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-400"
                      value={obj.goal}
                      onChange={(e) => updateObjective(i, 'goal', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Timeline</label>
                    <input
                      type="text"
                      className="w-full border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      value={obj.timeline}
                      onChange={(e) => updateObjective(i, 'timeline', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Resources</label>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {obj.resources.map((r, ri) => (
                        <span key={ri} className="flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                          {r}
                          <button type="button" onClick={() => removeResource(i, ri)} className="text-gray-400 hover:text-red-500">✕</button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        className="flex-1 border rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-400"
                        placeholder="Add resource and press Enter"
                        value={newResource[i] ?? ''}
                        onChange={(e) => setNewResource((prev) => ({ ...prev, [i]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addResource(i); } }}
                      />
                      <button type="button" onClick={() => addResource(i)}
                        className="px-3 py-1.5 text-xs bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200">
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Milestones */}
          <div>
            <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3">Milestones</h3>
            <div className="space-y-3">
              {milestones.map((m, i) => (
                <div key={i} className="border rounded-lg p-4 grid grid-cols-3 gap-3">
                  <div className="col-span-3">
                    <label className="block text-xs text-gray-500 mb-1">Label</label>
                    <input type="text"
                      className="w-full border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      value={m.label}
                      onChange={(e) => updateMilestone(i, 'label', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Due Date</label>
                    <input type="date"
                      className="w-full border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      value={m.dueDate ? m.dueDate.split('T')[0] : ''}
                      onChange={(e) => updateMilestone(i, 'dueDate', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Status</label>
                    <select
                      className="w-full border rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      value={m.status}
                      onChange={(e) => updateMilestone(i, 'status', e.target.value)}
                    >
                      <option value="pending">Pending</option>
                      <option value="in-progress">In Progress</option>
                      <option value="completed">Done</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div>
            <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">Notes for Employee</h3>
            <p className="text-xs text-gray-400 mb-2">Visible to the employee. Use this for personalized guidance, context, or encouragement.</p>
            <textarea
              rows={4}
              className="w-full border rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Add personalized notes for this employee…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t flex gap-3 justify-end shrink-0">
          <button type="button" onClick={onClose}
            className="px-4 py-2 text-sm border rounded-lg text-gray-600 hover:bg-gray-50">
            Cancel
          </button>
          <button type="button" onClick={handleSave} disabled={saving}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── IDP Readiness panel ─────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<string, string> = {
  position: 'Position',
  competency_requirements: 'Competency Requirements',
  competency_scores: 'Competency Scores',
  metrics: 'Performance Metrics',
  training_catalogue: 'Training Catalogue',
  milestone_templates: 'Milestone Templates',
};

function ReadinessPanel({
  readiness,
  loading,
  warningsAcknowledged,
  onAcknowledge,
}: {
  readiness: IDPReadinessResult | null;
  loading: boolean;
  warningsAcknowledged: boolean;
  onAcknowledge: () => void;
}) {
  if (loading) {
    return (
      <div className="mb-4 p-3 bg-gray-50 border rounded-lg animate-pulse flex items-center gap-2">
        <div className="w-3 h-3 rounded-full bg-gray-200" />
        <div className="h-3 bg-gray-200 rounded w-48" />
      </div>
    );
  }
  if (!readiness || readiness.items.length === 0) return null;

  const blocking = readiness.items.filter((i) => i.severity === 'blocking');
  const warnings = readiness.items.filter((i) => i.severity === 'warning');
  const hasBlocking = blocking.length > 0;
  const hasWarningsOnly = !hasBlocking && warnings.length > 0;

  return (
    <div className={`mb-4 border rounded-lg overflow-hidden ${hasBlocking ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}`}>
      <div className={`px-4 py-2.5 flex items-center gap-2 border-b ${hasBlocking ? 'border-red-200 bg-red-100/50' : 'border-amber-200 bg-amber-100/50'}`}>
        <span className="text-sm font-medium">
          {hasBlocking
            ? `⛔ ${blocking.length} issue${blocking.length !== 1 ? 's' : ''} must be resolved before generating IDP`
            : `⚠ ${warnings.length} warning${warnings.length !== 1 ? 's' : ''} — IDP quality may be limited`}
        </span>
      </div>
      <div className="divide-y divide-gray-100">
        {[...blocking, ...warnings].map((item, i) => (
          <div key={i} className="px-4 py-2.5 flex items-start gap-3">
            <span className={`mt-0.5 shrink-0 text-xs font-bold px-1.5 py-0.5 rounded ${item.severity === 'blocking' ? 'bg-red-200 text-red-800' : 'bg-amber-200 text-amber-800'}`}>
              {item.severity === 'blocking' ? '✗' : '!'}
            </span>
            <div className="min-w-0">
              <p className="text-xs font-medium text-gray-800">
                <span className="text-gray-500 mr-1">{CATEGORY_LABELS[item.category] ?? item.category}:</span>
                {item.message}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">→ {item.action}</p>
            </div>
          </div>
        ))}
      </div>
      {hasWarningsOnly && !warningsAcknowledged && (
        <div className="px-4 py-2.5 border-t border-amber-200 flex items-center justify-between">
          <p className="text-xs text-amber-700">Development objectives and milestones will be AI-inferred without your company's data — quality may be lower than expected.</p>
          <button
            type="button"
            onClick={onAcknowledge}
            className="ml-4 shrink-0 text-xs px-3 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-medium"
          >
            Generate anyway
          </button>
        </div>
      )}
      {hasWarningsOnly && warningsAcknowledged && (
        <div className="px-4 py-2.5 border-t border-amber-200">
          <p className="text-xs text-amber-600">✓ Acknowledged — you can now generate the IDP.</p>
        </div>
      )}
    </div>
  );
}

// ─── Employee selector panel ──────────────────────────────────────────────────

function EmployeePanel({
  employees, selectedId, onSelect, plans, generatingForUserId,
}: {
  employees: UserDto[]; selectedId: string;
  onSelect: (id: string) => void; plans: IDPPlan[];
  generatingForUserId: string | null;
}) {
  const hasPlan = (uid: string) => plans.some((p) => p.userId === uid);
  return (
    <div className="space-y-1">
      {employees.map((emp) => {
        const isGenerating = generatingForUserId === emp.id;
        return (
          <button key={emp.id} type="button" onClick={() => onSelect(emp.id)}
            className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors flex items-center justify-between ${
              selectedId === emp.id ? 'bg-blue-50 border border-blue-200 text-blue-800' : 'hover:bg-gray-50 border border-transparent text-gray-700'
            }`}>
            <span className="font-medium truncate">{emp.name}</span>
            <span className="flex items-center gap-1 shrink-0 ml-2">
              <span className="text-xs text-gray-400">{emp.role}</span>
              {isGenerating && (
                <span className="flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">
                  <span className="w-2 h-2 rounded-full border border-blue-500 border-t-transparent animate-spin inline-block" />
                  AI
                </span>
              )}
              {!isGenerating && hasPlan(emp.id) && (
                <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full">IDP</span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function IDPContent() {
  const { user } = useAuth();
  const companyId = user?.companyId ?? DEFAULT_COMPANY_ID;
  const userId = user?.id ?? '';
  const isHR = can(user?.role, 'view:wawasan');
  const isManager = user?.role === 'manager';
  const isHROrManager = isHR || isManager;

  const [employees, setEmployees] = useState<UserDto[]>([]);
  const [allPlans, setAllPlans] = useState<IDPPlan[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loading, setLoading] = useState(true);
  // Per-user generation tracking: stores the userId whose IDP is currently being generated.
  // null = no generation in progress.
  const [generatingForUserId, setGeneratingForUserId] = useState<string | null>(null);
  const [genProgress, setGenProgress] = useState(0);
  const [error, setError] = useState('');
  const [showMyDivision, setShowMyDivision] = useState(isManager);
  const [showEditModal, setShowEditModal] = useState(false);
  const [readiness, setReadiness] = useState<IDPReadinessResult | null>(null);
  const [readinessLoading, setReadinessLoading] = useState(false);
  // When warnings exist, user must acknowledge before generating
  const [warningsAcknowledged, setWarningsAcknowledged] = useState(false);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const effectiveUserId = isHROrManager ? selectedUserId : userId;

  // Is any employee's IDP currently being generated?
  const isAnyGenerating = generatingForUserId !== null;
  // Is the *currently viewed* employee being generated?
  const generatingForCurrent = generatingForUserId === effectiveUserId;

  const filteredEmployees = useMemo(() => {
    if (isManager && user?.divisionId) return employees.filter((e) => e.divisionId === user.divisionId);
    if (!showMyDivision || !user?.divisionId) return employees;
    return employees.filter((e) => e.divisionId === user.divisionId);
  }, [employees, isManager, showMyDivision, user?.divisionId]);

  const activePlan = useMemo(() => {
    if (!effectiveUserId) return null;
    return allPlans.find((p) => p.userId === effectiveUserId) ?? null;
  }, [allPlans, effectiveUserId]);

  useEffect(() => {
    let cancelled = false;
    async function init() {
      setLoading(true);
      try {
        if (isHROrManager) {
          const [empRes, plansRes] = await Promise.all([
            listUsers(companyId, { isActive: true }),
            listAllIDPs(companyId),
          ]);
          if (cancelled) return;
          if (empRes.success && empRes.data) setEmployees(empRes.data.users);
          if (plansRes.success && plansRes.data) setAllPlans(plansRes.data.plans);
          setSelectedUserId(userId);
        } else {
          const plansRes = await getIDPsForUser(userId, companyId);
          if (cancelled) return;
          if (plansRes.success && plansRes.data) setAllPlans(plansRes.data.plans);
          setSelectedUserId(userId);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    init();
    return () => { cancelled = true; };
  }, [companyId, userId, isHROrManager]); // eslint-disable-line

  // Fetch readiness when selected employee changes (HR/manager view only)
  useEffect(() => {
    if (!effectiveUserId || !isHROrManager) { setReadiness(null); return; }
    setReadinessLoading(true);
    setWarningsAcknowledged(false);
    getIDPReadiness(effectiveUserId, companyId).then((res) => {
      setReadinessLoading(false);
      if (res.success && res.data) setReadiness(res.data);
    }).catch(() => setReadinessLoading(false));
  }, [effectiveUserId, companyId, isHROrManager]); // eslint-disable-line

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current);
    };
  }, []);

  function stopPolling() {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    if (pollTimeoutRef.current) { clearTimeout(pollTimeoutRef.current); pollTimeoutRef.current = null; }
    setGeneratingForUserId(null);
    setGenProgress(0);
  }

  function startPolling(jobId: string, targetUserId: string) {
    if (pollRef.current) clearInterval(pollRef.current);

    // 90s timeout
    pollTimeoutRef.current = setTimeout(() => {
      stopPolling();
      toast.error('IDP generation is taking longer than expected. Please try again.');
    }, 90_000);

    pollRef.current = setInterval(async () => {
      const res = await processAIJob(jobId).catch(() => null);
      if (!res?.success || !res.data?.job) return;

      const job = res.data.job;
      setGenProgress(job.progress ?? 0);

      if (job.status === 'completed') {
        stopPolling();
        const result = job.result as any;
        // Refresh all plans from the server — correct regardless of which employee is viewed
        const fresh = await listAllIDPs(companyId).catch(() => null);
        if (fresh?.success && fresh.data) setAllPlans(fresh.data.plans);
        // Refresh readiness for the completed employee
        getIDPReadiness(targetUserId, companyId).then((r) => {
          if (r.success && r.data && targetUserId === effectiveUserId) setReadiness(r.data);
        }).catch(() => {});

        const empName = employees.find((e) => e.id === targetUserId)?.name ?? 'employee';
        if (result?.skipped) {
          toast.info(result.message ?? `No changes since last IDP generation for ${empName}.`);
        } else if (result?.plan || fresh?.success) {
          toast.success(`IDP generated for ${empName}.`);
        } else {
          toast.info('IDP generation completed.');
        }
      } else if (job.status === 'failed') {
        stopPolling();
        const empName = employees.find((e) => e.id === targetUserId)?.name ?? 'employee';
        toast.error(`Failed to generate IDP for ${empName}. Please try again.`);
      }
    }, 3000);
  }

  async function handleGenerate() {
    // Re-entry guard: if any generation is running, inform the user which employee it's for
    if (isAnyGenerating) {
      if (generatingForUserId !== effectiveUserId) {
        const busyName = employees.find((e) => e.id === generatingForUserId)?.name ?? 'another employee';
        toast.info(`Still generating IDP for ${busyName}. Please wait.`);
      }
      return;
    }
    if (!effectiveUserId) return;

    // Readiness guard: blocking items prevent generation
    if (readiness && !readiness.ready) {
      toast.error('Resolve the blocking issues listed above before generating an IDP.');
      return;
    }
    // Warnings must be acknowledged first
    if (readiness && readiness.items.some((i) => i.severity === 'warning') && !warningsAcknowledged) {
      toast.info('Review the warnings above and click "Generate anyway" to proceed.');
      return;
    }
    setError('');
    setGeneratingForUserId(effectiveUserId);
    setGenProgress(5);

    const res = await startIDPGeneration(effectiveUserId, companyId).catch(() => null);
    if (!res?.success || !res.data?.job) {
      stopPolling();
      const code = (res as any)?.code ?? '';
      if (code === 'job_in_progress') {
        const empName = employees.find((e) => e.id === effectiveUserId)?.name ?? 'this employee';
        toast.info(`IDP generation already in progress for ${empName}. Please wait.`);
        const existingJob = (res as any)?.data?.job;
        if (existingJob) {
          setGeneratingForUserId(effectiveUserId);
          startPolling(existingJob.id, effectiveUserId);
        }
      } else {
        const msg = res?.error ?? 'Failed to start IDP generation';
        setError(msg);
        toast.error(msg);
      }
      return;
    }

    startPolling(res.data.job.id, effectiveUserId);
  }

  async function handleStatusChange(status: IDPPlan['status']) {
    if (!activePlan) return;
    const res = await updateIDPStatus(activePlan.id, companyId, status);
    if (res.success) {
      setAllPlans((prev) => prev.map((p) => p.id === activePlan.id ? { ...p, status } : p));
      const labels: Record<IDPPlan['status'], string> = {
        draft: 'Reverted to draft',
        active: 'Plan activated',
        completed: 'Plan marked complete',
      };
      toast.success(labels[status]);
    } else {
      toast.error(res.error ?? 'Failed to update status');
    }
  }

  async function handleMilestoneClick(idx: number) {
    if (!activePlan) return;
    const updated = activePlan.milestones.map((m, i) =>
      i === idx ? { ...m, status: MILESTONE_CYCLE[m.status] } : m,
    );
    // Optimistic update
    setAllPlans((prev) => prev.map((p) => p.id === activePlan.id ? { ...p, milestones: updated } : p));
    const res = await updateMilestoneStatus(
      activePlan.id, companyId, userId, activePlan.userId, updated,
    );
    if (!res.success) {
      // Revert on error
      setAllPlans((prev) => prev.map((p) => p.id === activePlan.id ? { ...p, milestones: activePlan.milestones } : p));
      toast.error(res.error ?? 'Failed to update milestone');
    }
  }

  const selectedEmployee = employees.find((e) => e.id === effectiveUserId);
  const displayName = selectedEmployee?.name ?? user?.name ?? 'Employee';
  const canEditMilestones = isHROrManager || userId === activePlan?.userId;
  const generatingEmployeeName = employees.find((e) => e.id === generatingForUserId)?.name ?? 'employee';

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isHROrManager ? 'Individual Development Plans' : 'My Development Plan'}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            AI-generated competency gap analysis and development roadmaps
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      {isAnyGenerating && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin shrink-0" />
            <span className="text-sm text-blue-700 flex-1">
              AI is analysing <strong>{generatingEmployeeName}</strong>'s profile…
              {!generatingForCurrent && (
                <span className="text-blue-500"> (you're viewing {displayName})</span>
              )}
            </span>
            {genProgress > 0 && (
              <span className="text-xs text-blue-500 shrink-0">{genProgress}%</span>
            )}
          </div>
          <div className="mt-2 h-1 bg-blue-100 rounded-full overflow-hidden">
            <div
              className="h-1 bg-blue-400 rounded-full transition-all duration-500"
              style={{ width: `${genProgress}%` }}
            />
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Left: employee list */}
        {isHROrManager && (
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border shadow-sm p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Employees</h2>
                {isHR && !isManager && user?.divisionId && (
                  <button type="button" onClick={() => setShowMyDivision((v) => !v)}
                    className={`text-xs px-2 py-0.5 rounded-full border font-medium transition-colors ${
                      showMyDivision ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-gray-50 border-gray-200 text-gray-500'
                    }`}>
                    {showMyDivision ? 'My Division' : 'All'}
                  </button>
                )}
              </div>
              {loading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => <div key={i} className="h-10 bg-gray-100 rounded-lg animate-pulse" />)}
                </div>
              ) : (
                <EmployeePanel employees={filteredEmployees} selectedId={effectiveUserId}
                  onSelect={setSelectedUserId} plans={allPlans}
                  generatingForUserId={generatingForUserId} />
              )}
            </div>
          </div>
        )}

        {/* Right: IDP plan */}
        <div className={isHROrManager ? 'lg:col-span-3' : 'lg:col-span-4'}>
          <div className="bg-white rounded-xl border shadow-sm p-6 min-h-[500px]">
            {loading ? (
              <div className="space-y-3 animate-pulse">
                <div className="h-4 bg-gray-100 rounded w-1/2" />
                <div className="h-3 bg-gray-100 rounded w-3/4" />
                <div className="h-3 bg-gray-100 rounded w-2/3" />
              </div>
            ) : !effectiveUserId ? (
              <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                <p className="text-sm">Select an employee to view or generate an IDP</p>
              </div>
            ) : !activePlan ? (
              <div className="py-6 space-y-5">
                {/* Header */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
                    <ClipboardList size={20} className="text-blue-500" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">
                      {isHROrManager ? `No IDP yet for ${displayName}` : "You don't have a development plan yet"}
                    </p>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {isHROrManager
                        ? 'Generate an AI-powered development plan grounded in their performance data.'
                        : 'Your manager or HR will generate a personalised development plan for you.'}
                    </p>
                  </div>
                </div>

                {/* Readiness panel + generate button (HR/manager only) */}
                {isHROrManager && (
                  <div className="space-y-4">
                    <ReadinessPanel
                      readiness={readiness}
                      loading={readinessLoading}
                      warningsAcknowledged={warningsAcknowledged}
                      onAcknowledge={() => setWarningsAcknowledged(true)}
                    />
                    <button
                      type="button"
                      onClick={handleGenerate}
                      disabled={isAnyGenerating || (readiness != null && !readiness.ready)}
                      className="px-5 py-2.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
                    >
                      {generatingForCurrent ? 'Generating…' : 'Generate IDP'}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div>
                {/* Toolbar */}
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <h2 className="text-base font-semibold text-gray-900">
                    {isHROrManager ? `${displayName}'s Development Plan` : 'Your Development Plan'}
                  </h2>
                  <div className="flex gap-2 items-center flex-wrap">
                    <ExportDropdown
                      onPDF={() => printDocument(buildIDPPrintHTML(activePlan, displayName), `IDP — ${displayName}`)}
                      onJSON={() => downloadJSON(activePlan, `idp-${activePlan.userId}.json`)}
                    />

                    {/* Status buttons — all transitions */}
                    {isHROrManager && (
                      <>
                        {activePlan.status === 'draft' && (
                          <button type="button" onClick={() => handleStatusChange('active')}
                            className="text-xs px-3 py-1.5 border border-blue-200 text-blue-600 rounded-lg hover:bg-blue-50">
                            Activate Plan
                          </button>
                        )}
                        {activePlan.status === 'active' && (
                          <>
                            <button type="button" onClick={() => handleStatusChange('draft')}
                              className="text-xs px-3 py-1.5 border border-gray-200 text-gray-500 rounded-lg hover:bg-gray-50">
                              Revert to Draft
                            </button>
                            <button type="button" onClick={() => handleStatusChange('completed')}
                              className="text-xs px-3 py-1.5 border border-green-200 text-green-600 rounded-lg hover:bg-green-50">
                              Mark Complete
                            </button>
                          </>
                        )}
                        {activePlan.status === 'completed' && (
                          <button type="button" onClick={() => handleStatusChange('active')}
                            className="text-xs px-3 py-1.5 border border-blue-200 text-blue-600 rounded-lg hover:bg-blue-50">
                            Reopen Plan
                          </button>
                        )}
                        <button type="button" onClick={() => setShowEditModal(true)} disabled={isAnyGenerating}
                          className="text-xs px-3 py-1.5 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-40">
                          Edit Plan
                        </button>
                        <button
                          type="button"
                          onClick={handleGenerate}
                          disabled={isAnyGenerating || (readiness != null && !readiness.ready)}
                          className="text-xs px-3 py-1.5 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                          title={readiness && !readiness.ready ? 'Resolve blocking issues before regenerating' : undefined}
                        >
                          {generatingForCurrent ? 'Generating…' : 'Regenerate'}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Readiness panel shown inline when regenerating */}
                {isHROrManager && (
                  <ReadinessPanel
                    readiness={readiness}
                    loading={readinessLoading}
                    warningsAcknowledged={warningsAcknowledged}
                    onAcknowledge={() => setWarningsAcknowledged(true)}
                  />
                )}

                <IDPView
                  plan={activePlan}
                  canEditMilestones={canEditMilestones}
                  onMilestoneClick={handleMilestoneClick}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {showEditModal && activePlan && (
        <EditIDPModal
          plan={activePlan}
          companyId={companyId}
          editorUserId={userId}
          onSave={(updated) => {
            setAllPlans((prev) => prev.map((p) => p.id === updated.id ? updated : p));
            setShowEditModal(false);
          }}
          onClose={() => setShowEditModal(false)}
        />
      )}
    </div>
  );
}

function IDPPage() {
  return (
    <ProtectedRoute minimumRole="employee">
      <IDPContent />
    </ProtectedRoute>
  );
}
